import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, StatusBadge } from "@/components/dashboard/shared";
import { getAPIBaseURL, getAdminHeaders } from "@/lib/auth";

type KycStatus = "pending" | "approved" | "rejected";
type Filter = KycStatus | "all";

type Submission = {
  id: string;
  status: KycStatus;
  documentType: string;
  fullName: string;
  rejectionReason: string | null;
  createdAt: string;
  reviewedAt: string | null;
  user: { id: string; name: string; email: string; kycStatus: string };
};

type Counts = { pending: number; approved: number; rejected: number };

const FILTERS: { key: Filter; label: string }[] = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

const DOCUMENT_LABELS: Record<string, string> = {
  national_id: "National ID",
  passport: "Passport",
  driver_licence: "Driver's licence",
};

function formatDate(value: string | null) {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function apiError(json: { message?: string; error?: string }, fallback: string) {
  return json.message || json.error || fallback;
}

/** Loads a private KYC photo with the admin token; the image itself can't send headers. */
function useProtectedImage(path: string | null) {
  const [url, setUrl] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => {
    if (!path) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    setFailed(false);
    fetch(`${getAPIBaseURL()}${path}`, { headers: getAdminHeaders() })
      .then((response) => {
        if (!response.ok) throw new Error("load failed");
        return response.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setUrl(null);
    };
  }, [path]);
  return { url, failed };
}

function ProtectedPhoto({ label, path }: { label: string; path: string }) {
  const { url, failed } = useProtectedImage(path);
  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase text-muted-foreground">{label}</p>
      <div className="grid aspect-[4/3] place-items-center overflow-hidden rounded-xl border bg-muted/30">
        {url ? <img src={url} alt={label} className="h-full w-full object-contain" /> : <span className="text-sm text-muted-foreground">{failed ? "Photo unavailable" : "Loading…"}</span>}
      </div>
    </div>
  );
}

export function KycPage() {
  const [filter, setFilter] = React.useState<Filter>("pending");
  const [openId, setOpenId] = React.useState<string | null>(null);
  const queryClient = useQueryClient();

  const list = useQuery({
    queryKey: ["admin-kyc", filter],
    queryFn: async () => {
      const response = await fetch(`${getAPIBaseURL()}/api/v1/admin/kyc?status=${filter}&limit=100`, { headers: getAdminHeaders() });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(apiError(json, "Could not load verification requests"));
      return json as { submissions: Submission[]; counts: Counts };
    },
  });

  const rows = list.data?.submissions ?? [];
  const counts = list.data?.counts;

  return (
    <div className="space-y-5">
      <PageHeader title="Identity verification" description="Check each person's document and selfie. Approving shows the blue tick on their posts and profile." />

      <div className="grid gap-3 sm:grid-cols-3">
        {[
          { label: "Pending", value: counts?.pending ?? 0 },
          { label: "Approved", value: counts?.approved ?? 0 },
          { label: "Rejected", value: counts?.rejected ?? 0 },
        ].map((stat) => (
          <Card key={stat.label}>
            <CardContent className="p-4">
              <p className="text-xs font-semibold uppercase text-muted-foreground">{stat.label}</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">{list.isLoading ? "…" : stat.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((item) => (
          <Button key={item.key} size="sm" variant={filter === item.key ? "default" : "outline"} onClick={() => setFilter(item.key)}>
            {item.label}
          </Button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          {list.isLoading ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
            </div>
          ) : list.isError ? (
            <p className="p-6 text-sm text-destructive">{(list.error as Error).message}</p>
          ) : rows.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No requests in this list.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Person</TableHead>
                  <TableHead>Full name on document</TableHead>
                  <TableHead>Document</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Review</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <div className="font-medium">{row.user.name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{row.user.email}</div>
                    </TableCell>
                    <TableCell>{row.fullName}</TableCell>
                    <TableCell>{DOCUMENT_LABELS[row.documentType] ?? row.documentType}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{formatDate(row.createdAt)}</TableCell>
                    <TableCell>
                      <StatusBadge value={row.status} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" onClick={() => setOpenId(row.id)}>
                        {row.status === "pending" ? "Review" : "View"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {openId && <ReviewDialog id={openId} onClose={() => setOpenId(null)} onDone={() => queryClient.invalidateQueries({ queryKey: ["admin-kyc"] })} />}
    </div>
  );
}

function ReviewDialog({ id, onClose, onDone }: { id: string; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = React.useState("");
  const detail = useQuery({
    queryKey: ["admin-kyc-detail", id],
    queryFn: async () => {
      const response = await fetch(`${getAPIBaseURL()}/api/v1/admin/kyc/${id}`, { headers: getAdminHeaders() });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(apiError(json, "Could not load the request"));
      return json.submission as Submission;
    },
  });

  const decide = useMutation({
    mutationFn: async (status: "approved" | "rejected") => {
      const response = await fetch(`${getAPIBaseURL()}/api/v1/admin/kyc/${id}`, {
        method: "PATCH",
        headers: getAdminHeaders(),
        body: JSON.stringify(status === "rejected" ? { status, reason: reason.trim() } : { status }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(apiError(json, "Could not save the decision"));
    },
    onSuccess: (_data, status) => {
      toast.success(status === "approved" ? "Identity approved: blue tick is now shown" : "Request refused");
      onDone();
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const sub = detail.data;
  const pending = sub?.status === "pending";

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{sub ? sub.user.name || sub.user.email : "Verification request"}</DialogTitle>
          <DialogDescription>
            {sub ? `${DOCUMENT_LABELS[sub.documentType] ?? sub.documentType} · ${sub.fullName}` : "Loading…"}
          </DialogDescription>
        </DialogHeader>

        {detail.isError && <p className="text-sm text-destructive">{(detail.error as Error).message}</p>}

        {sub && (
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <ProtectedPhoto label="Document" path={sub.id ? `/api/v1/admin/kyc/${sub.id}/file/document` : ""} />
              <ProtectedPhoto label="Selfie" path={sub.id ? `/api/v1/admin/kyc/${sub.id}/file/selfie` : ""} />
            </div>
            <p className="text-sm">
              Status: <StatusBadge value={sub.status} />
              {sub.reviewedAt && <span className="ml-2 text-muted-foreground">Decided {formatDate(sub.reviewedAt)}</span>}
            </p>
            {sub.status === "rejected" && sub.rejectionReason && (
              <p className="text-sm text-muted-foreground">Reason given: {sub.rejectionReason}</p>
            )}
            {pending && (
              <div className="space-y-2">
                <p className="text-sm font-medium">If you refuse, write the reason the person will see</p>
                <Textarea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} placeholder="For example: the document photo is blurry" />
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {pending && (
            <>
              <Button variant="destructive" disabled={decide.isPending || reason.trim().length < 3} onClick={() => decide.mutate("rejected")}>
                Refuse
              </Button>
              <Button disabled={decide.isPending} onClick={() => decide.mutate("approved")}>
                Approve
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
